// =====================================================================
// Transfer Windows — pure helpers + constants.
// Date math, season-window aggregation, continent mapping.
// =====================================================================

export const MOBILE_MQ = "(max-width: 900px)";
export const PAGE_SIZE = 30;
export const DAY_MS = 86_400_000;

export const daysBetween = (a, b) => Math.round((b - a) / DAY_MS);
export const parseDate = (s) => (s ? new Date(s + "T00:00:00Z") : null);
export const fmtDate = (d) =>
  d
    ? d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const CONF_TO_CONTINENT = {
  UEFA: "europe",
  CAF: "africa",
  AFC: "asia",
  CONMEBOL: "south-america",
  CONCACAF: "north-america",
  OFC: "oceania",
};

export const CONTINENTS = [
  { id: "all", label: "All countries" },
  { id: "europe", label: "Europe" },
  { id: "africa", label: "Africa" },
  { id: "asia", label: "Asia" },
  { id: "south-america", label: "South America" },
  { id: "north-america", label: "North America" },
  { id: "oceania", label: "Oceania" },
];

export const CATEGORY_LABEL = {
  mens_professional: "Men's Pro",
  womens_professional: "Women's Pro",
  amateur: "Amateur",
};

// Pick the single window that drives a country's displayed status/dates.
// Priority: currently open > soonest upcoming > most recently closed.
// `forceLabel` ("summer"|"winter") narrows the candidate pool so the
// Summer/Winter tabs at the top of the page actually swap row dates.
export function pickPrimaryWindow(rowsForCountry, now, forceLabel = null) {
  const candidates = [];
  for (const r of rowsForCountry) {
    const w1s = parseDate(r.window_1_start);
    const w1e = parseDate(r.window_1_end);
    const w2s = parseDate(r.window_2_start);
    const w2e = parseDate(r.window_2_end);
    if (w1s && w1e)
      candidates.push({ row: r, start: w1s, end: w1e, label: r.window_1_label || "summer", which: 1 });
    if (w2s && w2e)
      candidates.push({ row: r, start: w2s, end: w2e, label: r.window_2_label || "winter", which: 2 });
  }
  const pool =
    forceLabel === "summer" || forceLabel === "winter"
      ? candidates.filter((c) => c.label === forceLabel)
      : candidates;
  if (pool.length === 0) return null;
  const active = pool.find((c) => c.start <= now && now <= c.end);
  if (active)
    return { ...active, state: "open", days: Math.max(0, daysBetween(now, active.end)) };
  const upcoming = pool.filter((c) => c.start > now).sort((a, b) => a.start - b.start)[0];
  if (upcoming)
    return { ...upcoming, state: "opens", days: daysBetween(now, upcoming.start) };
  const closed = pool.filter((c) => c.end < now).sort((a, b) => b.end - a.end)[0];
  if (closed) return { ...closed, state: "closed", days: 0 };
  return null;
}

// Reduce 1..many TransferWindow rows per country down to ONE display row.
// The latest season's dates populate the row + detail panel; the picked
// primary window drives the status badge.
export function aggregateByCountry(rows, now, windowFilter = "all") {
  const forceLabel =
    windowFilter === "summer" || windowFilter === "winter" ? windowFilter : null;
  const byCountry = new Map();
  for (const r of rows) {
    if (!r.country) continue;
    if (!byCountry.has(r.country)) byCountry.set(r.country, []);
    byCountry.get(r.country).push(r);
  }
  const out = [];
  for (const [country, list] of byCountry.entries()) {
    list.sort((a, b) =>
      (a.season_start || "").localeCompare(b.season_start || ""),
    );
    const latest = list[list.length - 1] || list[0];
    const primary = pickPrimaryWindow(list, now, forceLabel);
    out.push({
      id: latest.id,
      country,
      country_code: latest.country_code,
      country_image_url: latest.country_image_url,
      confederation: latest.confederation,
      category: latest.category,
      season: latest.season,
      season_start: latest.season_start,
      season_end: latest.season_end,
      window_1_start: latest.window_1_start,
      window_1_end: latest.window_1_end,
      window_2_start: latest.window_2_start,
      window_2_end: latest.window_2_end,
      window_1_label: latest.window_1_label,
      window_2_label: latest.window_2_label,
      primary,
    });
  }
  return out;
}

// Concise next-window estimate for a CLOSED row, e.g. "~Jan 2027".
// Repeats the most recent past window's month + year + 1. Deliberately short
// (abbreviated month, "~" = estimate) so it reads as a modest secondary hint
// next to a small "CLOSED" pill — not a dominant banner. NOTE: this estimate
// only appears because the CURRENT-season windows aren't in the data (the
// windows are imported from a CSV with no auto-refresh); the real fix is to
// re-import the current FIFA windows, not to hide this in the UI.
export function expectedFromClosed(country, now) {
  const p = country.primary;
  if (!p || p.state !== "closed") return null;
  const year = p.start.getUTCFullYear() + 1;
  const month = MONTHS[p.start.getUTCMonth()].slice(0, 3);
  return `~${month} ${year}`;
}
